/* eslint-disable @next/next/no-img-element */

import React, { useContext, useMemo } from 'react';

import { Cluster } from '@nerdwallet/currency/components/Cluster';
import { Text } from '@nerdwallet/currency/components/Text';
import { Tooltip } from '@nerdwallet/currency/components/Tooltip';
import { Title } from '@nerdwallet/currency/components/Title';
import classes from '@nerdwallet/base-styles/classes';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';
import HelpIcon from '@nerdwallet/react-icon/material/HelpOutline';
import appConfig from '@nerdwallet/app-config';
import ArrowIndicator, {
  SENTIMENTS,
} from '@components/arrow-indicator/arrow-indicator';
import SegmentImpression from '@components/segment-impression/segment-impression';
import { CreditHistory } from '../credit-history';
import styles from './credit-header.module.scss';
import { If } from 'react-if';
import TUTooltipContent from '@components/tu-tooltip/tu-tooltip';
import CreditScoreMeter from '@components/credit-meter/credit-meter';
import {
  ControlledTabs,
  useTabState,
} from '@nerdwallet/currency/components/Tabs';
import classNames from 'classnames';
import { getLogger } from '@lib/logging';
import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { gql } from '@apollo/client';
import { Box } from '@nerdwallet/currency/components/Box';
import { getDateDifference } from '@lib/utils';
import CurrentDateContext from '@lib/contexts/current-date-context';
import { capitalizeFirstLetter } from '@components/credit-meter/utils';
import { subMonths } from 'date-fns';

const tuLogo = `${appConfig.CDN_URL}/nodeapp/my-nerdwallet/images/tu-logo-blue.png`;

const tabs = {
  TODAY: 'today',
  HISTORY: 'history',
};

const getSentimentChange = (pointsDelta) => {
  if (pointsDelta === 0 && pointsDelta !== null) {
    return SENTIMENTS.NEUTRAL;
  }
  return pointsDelta > 0 ? SENTIMENTS.POSITIVE : SENTIMENTS.NEGATIVE;
};

const UpdatedTooltip = () => {
  return (
    <Tooltip
      id="help-updated-score"
      content={
        <div style={{ maxWidth: '17rem', whiteSpace: 'pre-wrap' }}>
          <h3 className={styles.tooltipHeading}>When is my score updated?</h3>
          <Text light>
            We pull your most updated score from TransUnion at least once a week
            on the same day. Note that this day might change if you get other
            alerts about your credit in the same week.
          </Text>
        </div>
      }
      placement="bottom"
    >
      <HelpIcon size={16} textAligned />
    </Tooltip>
  );
};

const CreditScoreUpdates = ({
  isFullContainer = false,
  creditScore,
  creditScoreBucket,
  pointDeltaSinceLastUpdate,
}) => (
  <React.Fragment>
    <Box
      className={classNames({
        [styles.fullScoreContainer]: isFullContainer,
      })}
    >
      <Cluster spacing={3} alignY="bottom">
        <Title>{creditScore}</Title>
        <Title size="small">{capitalizeFirstLetter(creditScoreBucket)}</Title>
      </Cluster>
    </Box>
    <Box marginBottom={2}>
      <Text size="small">
        <If
          condition={
            pointDeltaSinceLastUpdate !== 0 &&
            pointDeltaSinceLastUpdate !== null
          }
        >
          <ArrowIndicator
            sentiment={getSentimentChange(pointDeltaSinceLastUpdate)}
            className={classes('margin-right-1')}
          />
        </If>
        <span className={classes('margin-right-1')}>
          {Math.abs(pointDeltaSinceLastUpdate || 0)} pts
        </span>
        <span>
          <UpdatedTooltip />
        </span>
      </Text>
    </Box>
  </React.Fragment>
);

export const CREDIT_HEADER_QUERY = gql`
  query CreditHeader {
    tuCreditReportIdentityProfile {
      vantageScore3
      vantageScore3Bucket
      vantageScore3Delta
      reportPulledAt
    }
  }
`;

const logger = getLogger('CreditHeader');

interface CreditHeaderProps {}

export const CreditHeader: React.FC<CreditHeaderProps> = () => {
  const { currentDate } = useContext(CurrentDateContext);

  const { data, loading } = useQueryWithLog(logger, CREDIT_HEADER_QUERY);

  const creditScore = data?.tuCreditReportIdentityProfile?.vantageScore3;
  const creditScoreBucket =
    data?.tuCreditReportIdentityProfile?.vantageScore3Bucket;
  const pointDeltaSinceLastUpdate =
    data?.tuCreditReportIdentityProfile?.vantageScore3Delta;
  const lastUpdateString = useMemo(() => {
    if (!data?.tuCreditReportIdentityProfile?.reportPulledAt) {
      return '';
    }
    return `updated: ${getDateDifference(
      data.tuCreditReportIdentityProfile.reportPulledAt,
      currentDate,
    )} ago`;
  }, [data?.tuCreditReportIdentityProfile?.reportPulledAt, currentDate]);

  const startDate = subMonths(currentDate, 36).toISOString();

  const creditScoreMeterContainer = (
    <SegmentImpression eventName="Credit Score Today Viewed">
      <div className={styles.creditMeterContainer}>
        <CreditScoreMeter />
      </div>
    </SegmentImpression>
  );
  const creditHistoryContainer = (
    <SegmentImpression eventName="Credit Score History Viewed">
      <CreditHistory
        startDate={startDate}
        endDate={currentDate.toISOString()}
      />
    </SegmentImpression>
  );

  const historyContainer = () => {
    return (
      <div className={styles.historyTab}>
        {creditHistoryContainer}
        <div className={styles.historyTabBottomContainer}>
          <Text light size="small" className={styles.mobileLastUpdated}>
            {lastUpdateString}
          </Text>
          <Tooltip
            id="dashboard-header-tu-tooltip"
            content={TUTooltipContent()}
          >
            <img src={tuLogo} className={styles.tuLogo} alt="TransUnion" />
          </Tooltip>
        </div>
      </div>
    );
  };

  const tabState = useTabState({
    defaultTabIndex: 0,
    tabs: [
      {
        label: tabs.TODAY,
        content: creditScoreMeterContainer,
      },
      {
        label: tabs.HISTORY,
        content: historyContainer(),
      },
    ],
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className={styles.mobileScoreContainer}>
        <CreditScoreUpdates
          creditScore={creditScore}
          creditScoreBucket={creditScoreBucket}
          pointDeltaSinceLastUpdate={pointDeltaSinceLastUpdate}
        />
        <Box className={styles.tabContainer}>
          <ControlledTabs
            tabState={tabState}
            hasSpacingBetweenTabsAndContent
            hasBorderBetweenTabsAndContent
          />
        </Box>
      </div>

      <div className={styles.fullContainer}>
        <div className={styles.meterLeft}>
          <CreditScoreUpdates
            creditScore={creditScore}
            creditScoreBucket={creditScoreBucket}
            pointDeltaSinceLastUpdate={pointDeltaSinceLastUpdate}
            isFullContainer
          />
          {creditScoreMeterContainer}
        </div>
        <div className={styles.historyRight}>
          <Text bold>History</Text>
          {creditHistoryContainer}
        </div>
        <div className={styles.clearfix} />
      </div>
    </>
  );
};
